import { Link } from '@tanstack/react-router';
import { BRAND } from '@maintenance-tracker/shared';
import { cn } from '@/lib/utils';

const SIZES = {
  sm: { box: 'h-8 w-8 rounded-lg', icon: 'h-4 w-4', text: 'text-sm font-semibold' },
  md: { box: 'h-9 w-9 rounded-lg', icon: 'h-[18px] w-[18px]', text: 'text-sm font-semibold' },
  lg: { box: 'h-12 w-12 rounded-xl', icon: 'h-6 w-6', text: 'text-xl font-semibold' },
} as const;

type Size = keyof typeof SIZES;

export function BrandMark({
  size = 'md',
  className,
}: {
  size?: Size;
  className?: string;
}) {
  const s = SIZES[size];
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden ring-1 ring-white/15 shadow-[0_4px_14px_-4px_rgba(43,84,228,0.75)]',
        s.box,
        className,
      )}
      aria-hidden
    >
      <span className="absolute inset-0 bg-gradient-to-br from-accent-bright via-accent to-accent-hover" />
      <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent" />
      <svg viewBox="0 0 28 28" fill="none" className={cn('relative text-white', s.icon)}>
        <path
          d="M5 19 L11.5 12.5 L15 16 L23 8"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M18.5 8 H23 V12.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="5" cy="19" r="1.5" fill="currentColor" />
      </svg>
    </div>
  );
}

type BrandLogoProps = {
  size?: Size;
  showName?: boolean;
  /** Sidebar / dark backgrounds */
  inverted?: boolean;
  className?: string;
  nameClassName?: string;
  to?: string;
};

export function BrandLogo({
  size = 'md',
  showName = true,
  inverted = false,
  className,
  nameClassName,
  to = '/',
}: BrandLogoProps) {
  const s = SIZES[size];
  const inner = (
    <>
      <BrandMark size={size} />
      {showName && (
        <span
          className={cn(
            s.text,
            inverted ? 'text-white' : 'text-text-primary',
            nameClassName,
          )}
        >
          {BRAND.name}
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cn('flex items-center gap-2.5', className)}>
        {inner}
      </Link>
    );
  }

  return <div className={cn('flex items-center gap-2.5', className)}>{inner}</div>;
}
